import { useRef, useState } from 'react';
import { Camera, X, Loader2, User } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { api } from '@/lib/api';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';

type Shape = 'circle' | 'square';

type CommonProps = {
  value?: string | null;
  shape?: Shape;
  /** Pixel size for the avatar/box. Defaults to 96. */
  size?: number;
  /** Initials shown when no image is set (circle shape only). */
  fallback?: string;
  label?: string;
  helperText?: string;
  disabled?: boolean;
  /** Megabytes. Defaults to 5. */
  maxSizeMB?: number;
  /** Comma-separated MIME types. Defaults to common image types. */
  accept?: string;
};

type TwoStepProps = CommonProps & {
  mode?: 'two-step';
  /**
   * FileResourcePurpose enum value to send when creating the file_resource.
   * e.g. 604 for USER_AVATAR, 603 for ORGANIZATIONAL_UNIT_LOGO.
   */
  purpose: number;
  /**
   * Called after the file_resource is created. Parent is responsible for
   * persisting `fileResourceId` on the owning entity (e.g. PATCH /me/avatar).
   */
  onUploaded: (fileResourceId: number, url: string) => Promise<void> | void;
  /** Called when the user removes the image. Parent clears the FK. */
  onRemove: () => Promise<void> | void;
};

type DirectProps = CommonProps & {
  mode: 'direct';
  /** Endpoint to POST a multipart "file" to. e.g. `/workspaces/12/logo`. */
  uploadUrl: string;
  /** Endpoint to DELETE the image at. e.g. `/workspaces/12/logo`. */
  deleteUrl?: string;
  /**
   * Extracts the new image URL from the server response. Defaults to
   * `response.data.logo_url`.
   */
  pickUrl?: (response: any) => string | null | undefined;
  /** Notified after a successful upload or removal. */
  onChanged?: (url: string | null) => void;
};

export type EntityImageUploadProps = TwoStepProps | DirectProps;

const DEFAULT_ACCEPT = 'image/jpeg,image/jpg,image/png,image/gif,image/webp';
const DEFAULT_MAX_MB = 5;

export function EntityImageUpload(props: EntityImageUploadProps) {
  const {
    value,
    shape = 'circle',
    size = 96,
    fallback,
    label,
    helperText,
    disabled = false,
    maxSizeMB = DEFAULT_MAX_MB,
    accept = DEFAULT_ACCEPT,
  } = props;

  const [previewUrl, setPreviewUrl] = useState<string | null>(value ?? null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const busy = uploading || removing || disabled;
  const displayedUrl = previewUrl ?? value ?? null;

  const validate = (file: File): string | null => {
    const validTypes = accept.split(',').map((t) => t.trim());
    if (!validTypes.includes(file.type)) {
      return `Unsupported file type. Allowed: ${validTypes.join(', ')}`;
    }
    const maxBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxBytes) {
      return `File must be smaller than ${maxSizeMB}MB`;
    }
    return null;
  };

  const readDataUrl = (file: File): Promise<string> =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(file);
    });

  const handleFile = async (file: File) => {
    const validationError = validate(file);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    const localPreview = await readDataUrl(file);
    setPreviewUrl(localPreview);
    setUploading(true);

    try {
      if (props.mode === 'direct') {
        const form = new FormData();
        form.append('file', file);
        const response = await api.post<any>(props.uploadUrl, form);
        const pickUrl = props.pickUrl ?? ((r: any) => r?.data?.logo_url ?? null);
        const newUrl = pickUrl(response) ?? null;
        setPreviewUrl(newUrl);
        props.onChanged?.(newUrl);
        toast.success('Image uploaded');
      } else {
        const form = new FormData();
        form.append('file', file);
        form.append('purpose', String(props.purpose));
        const response = await api.post<{ data?: { id: number; url: string } }>(
          '/file-resources',
          form,
        );
        const id = response?.data?.id;
        const url = response?.data?.url;
        if (!id || !url) {
          throw new Error('Upload succeeded but server response was malformed');
        }
        await props.onUploaded(id, url);
        setPreviewUrl(url);
        toast.success('Image uploaded');
      }
    } catch (err: any) {
      setPreviewUrl(value ?? null);
      toast.error(err?.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    if (busy) return;
    setRemoving(true);
    try {
      if (props.mode === 'direct') {
        if (props.deleteUrl) {
          await api.delete(props.deleteUrl);
        }
        setPreviewUrl(null);
        props.onChanged?.(null);
      } else {
        await props.onRemove();
        setPreviewUrl(null);
      }
      toast.success('Image removed');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to remove image');
    } finally {
      setRemoving(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (busy) return;
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (busy) return;
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const openPicker = () => {
    if (busy) return;
    inputRef.current?.click();
  };

  const sizeStyle = { width: size, height: size };

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-medium text-gray-700">{label}</label>
      )}

      {/* Outer wrapper hosts the group + the (un-clipped) remove button.
         The inner dropzone keeps overflow-hidden so the image stays clipped
         to the shape, but the X button can now sit on the corner without
         being cut off. */}
      <div className="group relative" style={sizeStyle}>
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={openPicker}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled}
          aria-label={label ?? 'Upload image'}
          className={cn(
            'relative h-full w-full overflow-hidden border-2 transition-colors',
            shape === 'circle' ? 'rounded-full' : 'rounded-lg',
            busy ? 'cursor-not-allowed' : 'cursor-pointer',
            dragOver
              ? 'border-pink-400 bg-pink-50'
              : displayedUrl
                ? 'border-gray-200'
                : 'border-dashed border-gray-300 hover:border-pink-400 hover:bg-pink-50/40',
          )}
        >
          {displayedUrl ? (
            shape === 'circle' ? (
              <Avatar className="h-full w-full">
                <AvatarImage src={displayedUrl} alt={label ?? 'avatar'} />
                <AvatarFallback>{fallback ?? <User className="h-6 w-6" />}</AvatarFallback>
              </Avatar>
            ) : (
              <img
                src={displayedUrl}
                alt={label ?? 'image'}
                className="h-full w-full object-cover"
              />
            )
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center gap-1 bg-gray-50 text-gray-400">
              {shape === 'circle' && fallback ? (
                <span className="text-lg font-semibold">{fallback}</span>
              ) : (
                <Camera className="h-6 w-6" />
              )}
              <span className="text-[10px] uppercase tracking-wide">Upload</span>
            </div>
          )}

          {!disabled && (
            <div
              className={cn(
                'absolute inset-0 flex items-center justify-center bg-black/40 text-white opacity-0 transition-opacity',
                !uploading && 'group-hover:opacity-100',
                uploading && 'opacity-100',
              )}
            >
              {uploading ? (
                <Loader2 className="h-6 w-6 animate-spin" />
              ) : (
                <Camera className="h-6 w-6" />
              )}
            </div>
          )}
        </div>

        {displayedUrl && !disabled && !uploading && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              void handleRemove();
            }}
            disabled={removing}
            aria-label="Remove image"
            className={cn(
              'absolute right-1 top-1 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white shadow ring-2 ring-white transition-opacity hover:bg-red-600',
              'opacity-0 group-hover:opacity-100',
              removing && 'opacity-100',
            )}
          >
            {removing ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleFileSelect}
        disabled={busy}
        className="hidden"
      />

      {helperText && <p className="text-xs text-gray-500">{helperText}</p>}
    </div>
  );
}
